import { motion } from "framer-motion";
import {
  Store,
  Warehouse,
  Factory,
  Stethoscope,
  GraduationCap,
  HardHat,
  Truck,
  UtensilsCrossed,
  ArrowUpRight,
} from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";

const INDUSTRIES = [
  { Icon: Store, name: "Retail", img: "https://images.pexels.com/photos/36730429/pexels-photo-36730429.jpeg?auto=compress&cs=tinysrgb&w=800" },
  { Icon: Warehouse, name: "Wholesale", img: "https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?auto=compress&cs=tinysrgb&w=800" },
  { Icon: Factory, name: "Manufacturing", img: "https://images.pexels.com/photos/34718930/pexels-photo-34718930.jpeg?auto=compress&cs=tinysrgb&w=800" },
  { Icon: Stethoscope, name: "Healthcare", img: "https://images.unsplash.com/photo-1584982751601-97dcc096659c?auto=format&fit=crop&w=800&q=80" },
  { Icon: GraduationCap, name: "Education", img: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=800&q=80" },
  { Icon: HardHat, name: "Construction", img: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=800&q=80" },
  { Icon: Truck, name: "Transport", img: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=800&q=80" },
  { Icon: UtensilsCrossed, name: "Hospitality", img: "https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=800&q=80" },
];

export default function Industries() {
  return (
    <section id="industries" data-testid="industries-section" className="relative py-24 lg:py-32 bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          variants={stagger(0.1, 0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div className="max-w-xl">
            <motion.span variants={fadeUp} className="eyebrow"><span className="dot" /> Industry Insights</motion.span>
            <motion.h2 variants={fadeUp} className="mt-6 font-display font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[#1A1A2E]">
              Built for every{" "}
              <span className="font-serif-accent text-[#0D47A1]">business type.</span>
            </motion.h2>
          </div>
          <motion.p variants={fadeUp} className="max-w-md text-[16px] text-[#4A5568] leading-relaxed">
            From single-shop retail to multi-plant manufacturing — TallyPrime adapts to your world.
          </motion.p>
        </motion.div>

        <motion.div
          variants={stagger(0.05, 0.06)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5"
        >
          {INDUSTRIES.map(({ Icon, name, img }, i) => (
            <motion.a
              key={name}
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
              }}
              variants={fadeUp}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`industry-card-${i}`}
              className="group relative rounded-2xl overflow-hidden aspect-[4/5] cursor-pointer bg-[#0D47A1] shadow-[0_10px_30px_rgba(13,71,161,0.1)]"
            >
              <img
                src={img}
                alt={name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-[1.08] transition-all duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D47A1] via-[#0D47A1]/50 to-transparent" />
              <div className="relative h-full flex flex-col justify-between p-5">
                <div className="w-11 h-11 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center backdrop-blur-sm">
                  <Icon className="w-5 h-5 text-white" strokeWidth={1.75} />
                </div>
                <div className="flex items-end justify-between">
                  <h3 className="font-display font-bold text-white text-2xl tracking-tight">{name}</h3>
                  <ArrowUpRight className="w-5 h-5 text-white/80 opacity-0 group-hover:opacity-100 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500" />
                </div>
              </div>
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
